import { a as autoUpdate, o as offset, b as shift, f as flip, d as autoPlacement, e as arrow, c as computePosition } from './floating-ui.dom-SMWHQ3Bc.js';
import { d as debounce } from './debounce-CFN_LA0O.js';

/**
 * Tooltip web component powered by Floating UI.
 *
 * @element tooltip-element
 * @attr {string} id              - Required. Connects to trigger via `data-tooltip-id`.
 * @attr {number} [offset=16]     - Distance in px between trigger and tooltip.
 * @attr {string} [placement]     - Explicit Floating UI placement (e.g. "top", "bottom-start"). Uses autoPlacement when omitted.
 * @attr {boolean} [lock-placement] - When present, never flips away from `placement`, even when it does not fit.
 * @attr {boolean} [arrow]        - When present, renders a decorative arrow element.
 * @attr {string} [data-state]    - Set to "open" before connecting to render permanently visible with no trigger/hover required, positioned against the parent element.
 * @attr {string} [data-positioned] - Set by the component once Floating UI has written a position. Until then the tooltip stays hidden.
 *
 * @example
 * <button data-tooltip-id="tip">Hover me</button>
 * <tooltip-element id="tip">Helpful text</tooltip-element>
 *
 * @example
 * <div>
 *   <tooltip-element data-state="open">Always visible</tooltip-element>
 * </div>
 */
class TooltipElement extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = document.querySelector(`[data-tooltip-id="${this.id}"]`);

  /** @type {HTMLElement | undefined} */
  #arrowEl;

  /** @type {(() => void) | null} */
  #cleanup;

  /** @type {Promise<void>} */
  #positioned = Promise.resolve();

  /** @type {(event: KeyboardEvent) => void} */
  #boundEscapeHandler = (event) => {
    if (event.key === 'Escape') this.#hide();
  };

  connectedCallback() {
    this.role = 'tooltip';

    if (this.#trigger) {
      this.#trigger.setAttribute('aria-describedby', this.id);
    }

    if (this.hasAttribute('arrow')) {
      this.#createArrow();
    }

    if (this.getAttribute('data-state') === 'open') {
      this.#show();
    } else if (this.#trigger) {
      this.#bindEventListeners();
    }
  }

  disconnectedCallback() {
    this.#cleanup?.();
  }

  /**
   * Reference element the tooltip positions itself against. Falls back to the
   * parent element when there's no trigger, e.g. a permanently visible tooltip.
   * @returns {HTMLElement}
   */
  get #anchor() {
    return this.#trigger || this.parentElement;
  }

  /**
   * Resolves after Floating UI writes the next left/top.
   * @returns {Promise<void>}
   */
  get positioned() {
    return this.#positioned;
  }

  /**
   * Offset distance between the trigger and the tooltip.
   * @returns {number}
   */
  get #offset() {
    const offset = this.getAttribute('offset') ? Number(this.getAttribute('offset')) : 16;
    return offset;
  }

  /**
   * Explicit Floating UI placement. Returns `null` when unset, which triggers autoPlacement.
   * @returns {import('@floating-ui/dom').Placement | null}
   */
  get #placement() {
    return this.getAttribute('placement') || null;
  }

  /**
   * Whether the requested placement must survive a boundary with no room for it, e.g. a tooltip
   * anchored inside a clipped element that is shorter than the tooltip itself.
   * @returns {boolean}
   */
  get #lockedPlacement() {
    return this.hasAttribute('lock-placement');
  }

  /**
   * Binds event listeners to the tooltip and trigger.
   */
  #bindEventListeners() {
    const showEvents = [
      ['mouseenter', this.#show.bind(this)],
      ['focus', this.#show.bind(this)],
    ];

    const hideEvents = [
      ['mouseleave', debounce(() => this.#hide(), 300)],
      ['blur', debounce(() => this.#hide(), 300)],
    ];

    showEvents.forEach(([event, callback]) => {
      this.#trigger.addEventListener(event, callback);
    });
    hideEvents.forEach(([event, callback]) => {
      this.addEventListener(event, callback);
      this.#trigger.addEventListener(event, callback);
    });
  }

  /**
   * Creates the arrow element and appends it to the tooltip.
   */
  #createArrow() {
    this.#arrowEl = document.createElement('div');
    this.#arrowEl.setAttribute('data-arrow', '');
    this.#arrowEl.setAttribute('aria-hidden', 'true');
    this.appendChild(this.#arrowEl);
  }

  /**
   * Shows the tooltip and starts auto-updating its position.
   */
  #show() {
    this.setAttribute('data-state', 'open');
    this.#cleanup = autoUpdate(this.#anchor, this, () => this.#updatePosition());

    if (this.#trigger) {
      document.addEventListener('keydown', this.#boundEscapeHandler);
    }
  }

  /**
   * Hides the tooltip and stops position updates.
   */
  #hide() {
    const isOverTooltip =
      this.matches(':hover') || this.matches(':focus-within') || this.matches(':focus');
    if (isOverTooltip) return;

    this.setAttribute('data-state', 'closed');
    this.#cleanup?.();
    this.#cleanup = null;
    document.removeEventListener('keydown', this.#boundEscapeHandler);
  }

  /**
   * Assembles the Floating UI middleware stack based on component attributes.
   * @returns {import('@floating-ui/dom').Middleware[]}
   */
  #buildMiddleware() {
    /** @type {number} */
    const padding = 8;

    /** @type {import('@floating-ui/dom').Middleware[]} */
    const middleware = [offset(this.#offset), shift({ padding })];

    if (this.#placement) {
      // Locked tooltips skip flip entirely; any fallback would move the bubble — and its arrow —
      // to the opposite side of the anchor.
      if (!this.#lockedPlacement) {
        middleware.push(
          flip({ padding, crossAxis: 'alignment', fallbackAxisSideDirection: 'end' })
        );
      }
    } else {
      middleware.push(
        autoPlacement({ padding, crossAxis: 'alignment', fallbackAxisSideDirection: 'end' })
      );
    }

    if (this.#arrowEl) {
      middleware.push(arrow({ element: this.#arrowEl }));
    }

    return middleware;
  }

  /**
   * Computes and applies the tooltip position relative to the trigger.
   */
  #updatePosition() {
    /** @type {import('@floating-ui/dom').ComputePositionConfig} */
    const options = {
      middleware: this.#buildMiddleware(),
      ...(this.#placement && { placement: this.#placement }),
    };

    this.#positioned = computePosition(this.#anchor, this, options).then(
      ({ x, y, placement, middlewareData }) => {
        Object.assign(this.style, { left: `${x}px`, top: `${y}px` });

        if (this.#arrowEl && middlewareData.arrow) {
          this.#positionArrow(placement, middlewareData.arrow);
        }

        this.setAttribute('data-positioned', '');
      }
    );
  }

  /**
   * Positions the arrow element on the opposite side of the tooltip's placement.
   * @param {import('@floating-ui/dom').Placement} placement - Resolved placement from Floating UI.
   * @param {{ x?: number | null, y?: number | null }} coords - Arrow coordinates from the arrow middleware.
   */
  #positionArrow(placement, { x: arrowX, y: arrowY }) {
    /** @type {Record<string, string>} */
    const opposites = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' };
    const staticSide = opposites[placement.split('-')[0]];

    Object.assign(this.#arrowEl.style, {
      left: arrowX !== null ? `${arrowX}px` : '',
      top: arrowY !== null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: `-4px`,
    });
  }
}

if (!customElements.get('tooltip-element')) {
  customElements.define('tooltip-element', TooltipElement);
}
